/**
 * A dialog over the page: Escape or a click outside closes it, focus goes into it and comes back
 * to where it was. For short tasks that should not take the person away from where they are.
 */

import { useEffect, useRef, type ReactNode } from 'react'
import { X } from 'lucide-react'
import { useI18n } from '../lib/i18n'

export function Modal({ title, onClose, children, wide = false }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  const { t } = useI18n()
  const box = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const before = document.activeElement as HTMLElement | null
    box.current?.querySelector<HTMLElement>('input, button:not(.modal__close), a, textarea, select')?.focus()
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('keydown', onKey)
      before?.focus?.()
    }
  }, [onClose])

  return (
    <div
      className="modal__backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div ref={box} className={`panel modal${wide ? ' modal--wide' : ''}`} role="dialog" aria-modal="true" aria-label={title}>
        <header className="panel__header">
          <h2 className="panel__title">{title}</h2>
          <div className="spacer" />
          <button type="button" className="btn btn--sm btn--ghost btn--icon modal__close" onClick={onClose} aria-label={t('common.close')}>
            <X size={14} />
          </button>
        </header>
        <div className="panel__body modal__body">{children}</div>
      </div>
    </div>
  )
}
